import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import { startOfToday, format } from 'date-fns'

import { BorderLinearProgress } from "../../common/utilities/customMaterialUI"
import ParsoIco from "../../common/components/Svgs/ParsoIco"
import PendingVisitsModal from '../../common/components/PendingVisitsModal/PendingVisitsModal'
import PendingInvitationsModal from '../../common/components/PendingInvitationsModal/PendingInvitationsModal'
import { api } from '../../api/api'
import { Types } from '../../state/actionTypes'
import { _dispatch as dispatch, defaultCatch, defaultSuccess } from '../../state/actions'
import db, { deletePendingVisitPhotos, getSyncWindowStartDate } from '../../db/db'
import { syncEntity, runSync, pullTodayInvitations, withSyncLock, COMES_FROM_LOCAL_LIMIT } from '../../sync/SyncEngine'
import './LoadPage.sass'

function LoadPage(props) {
  const {
    dispatch,
    defaultCatch,
    defaultSuccess,
    enabled_visit_host,
  } = props

  const navigate = useNavigate()
  const [quarantinedVisits, setQuarantinedVisits] = useState([])
  const [showPendingVisitsModal, setShowPendingVisitsModal] = useState(false)
  const [quarantinedInvitations, setQuarantinedInvitations] = useState([])
  const [showPendingInvitationsModal, setShowPendingInvitationsModal] = useState(false)

  // Se muestran en serie (primero visitas, luego invitaciones) para no
  // abrumar al usuario con dos modales bloqueantes simultaneos. Llamado tanto
  // al terminar el sync inicial (si no habia visitas en cuarentena) como
  // despues de resolver el modal de visitas (sync o discard).
  const proceedAfterQuarantinedVisits = async () => {
    const quarantinedInv = await db.quarantined_invitations.toArray()
    if (quarantinedInv.length > 0) {
      setQuarantinedInvitations(quarantinedInv)
      setShowPendingInvitationsModal(true)
    } else {
      navigate('/')
    }
  }

  const fetchCompany = () => {
    return api.get('/companies')
      .then(defaultSuccess)
      .then(({ data }) => {
        if (!data.success) return
        dispatch({ type: Types.SET_COMPANIES, payload: data.companies })
      })
      .catch(error => defaultCatch(error, navigate))
  }

  const fetchVisitDestinations = () => {
    return api.get('/visit_destinations')
      .then(defaultSuccess)
      .then(({ data }) => {
        if (!data.success) return
        dispatch({ type: Types.SET_VISIT_DESTINATIONS, payload: data.destinations })
      })
      .catch(error => defaultCatch(error, navigate))
  }

  const fetchBuildings = () => {
    return api.get('/buildings')
      .then(defaultSuccess)
      .then(({ data }) => {
        if (!data.success) return
        dispatch({ type: Types.SET_BUILDINGS, payload: data.buildings })
      })
      .catch(error => defaultCatch(error, navigate))
  }

  useEffect(() => {
    const run = async () => {
      try {
        // 1. Datos de referencia en Redux (sin cambio)
        await Promise.all([
          fetchCompany(),
          fetchBuildings(),
          fetchVisitDestinations(),
        ])

        // 2. Sync incremental a IndexedDB. Espera el lock `lobby-sync` si otra
        // pestaña está sincronizando: saltarse esta descarga dejaría el
        // kiosko sin datos.
        await withSyncLock(() => {
          const todayStr = format(startOfToday(), 'yyyy-MM-dd')

          const syncTasks = [
            // Visitas: desde el checkpoint guardado o, si no hay, desde hoy.
            // `initialCheckpoint` solo fija la primera página; las siguientes
            // siguen el cursor del servidor, así que trae todas las
            // actualizadas hoy y no solo las primeras 100.
            syncEntity({
              endpoint: '/visits/batch',
              table: db.visits,
              dataKey: 'visits',
              initialCheckpoint: todayStr,
              params: { since_date: getSyncWindowStartDate() },
            }),
            // Invitaciones de hoy, completas: un checkpoint de la sesión
            // anterior omitiría las actualizadas antes de esa hora.
            pullTodayInvitations({ full: true }),
            syncEntity({
              endpoint: '/black_lists',
              table: db.black_lists,
              dataKey: 'black_lists',
            }),
            syncEntity({
              endpoint: '/visits/comes/all',
              table: db.comes_from,
              dataKey: 'comes',
              params: { limit: COMES_FROM_LOCAL_LIMIT },
            }),
          ]

          if (enabled_visit_host) {
            syncTasks.push(syncEntity({
              endpoint: '/employees',
              table: db.employees,
              dataKey: 'users',
            }))
          }

          return Promise.all(syncTasks)
        })
      } catch (error) {
        // Si falla el sync pero hay datos locales, continuar
        const hasLocalData = await db.sync_meta.count()
        if (!hasLocalData) {
          console.error('[LoadPage] Sin datos locales y sin red:', error)
          // Mostrar pantalla de error se maneja en el render
        }
      } finally {
        // Visitas/invitaciones que quedaron sin sincronizar en la sesión
        // anterior (ver clearLocalDatabase en db.js) — se le pregunta al
        // usuario si desea reintentar sincronizarlas o descartarlas antes de
        // continuar.
        const quarantined = await db.quarantined_visits.toArray()
        if (quarantined.length > 0) {
          setQuarantinedVisits(quarantined)
          setShowPendingVisitsModal(true)
        } else {
          await proceedAfterQuarantinedVisits()
        }
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSyncQuarantined = async () => {
    const restored = quarantinedVisits.map(({ quarantinedAt, ...visit }) => ({
      ...visit,
      syncStatus: 'pending',
      syncError: null,
      pushAttempts: 0,
    }))
    await db.visits.bulkPut(restored)
    await db.quarantined_visits.clear()
    setShowPendingVisitsModal(false)
    // Fire-and-forget: useSyncEngine's own effects are live by now too, and
    // runSync() is idempotent-safe (isSyncing guard) if both race.
    runSync({ syncEmployees: !!enabled_visit_host })
      .catch(err => console.error('[LoadPage] Error al sincronizar visitas en cuarentena:', err))
    await proceedAfterQuarantinedVisits()
  }

  const handleDiscardQuarantined = async () => {
    // `clearLocalDatabase` conservó las fotos de estas visitas por si el
    // usuario elegía reintentar; al descartarlas ya no tienen destino.
    await deletePendingVisitPhotos(quarantinedVisits.map(visit => visit.uuid))
    await db.quarantined_visits.clear()
    setShowPendingVisitsModal(false)
    await proceedAfterQuarantinedVisits()
  }

  const handleSyncQuarantinedInvitations = async () => {
    const restored = quarantinedInvitations.map(({ quarantinedAt, ...invitation }) => ({
      ...invitation,
      syncStatus: 'pending',
      syncError: null,
      pushAttempts: 0,
    }))
    await db.invitations.bulkPut(restored)
    await db.quarantined_invitations.clear()
    setShowPendingInvitationsModal(false)
    // Fire-and-forget, misma razon que `handleSyncQuarantined`.
    runSync({ syncEmployees: !!enabled_visit_host })
      .catch(err => console.error('[LoadPage] Error al sincronizar pre-registros en cuarentena:', err))
    navigate('/')
  }

  const handleDiscardQuarantinedInvitations = async () => {
    await db.quarantined_invitations.clear()
    setShowPendingInvitationsModal(false)
    navigate('/')
  }

  return (
    <div className="LoadPage">
      <div className="box-loading">
        <ParsoIco width={75} />
        <BorderLinearProgress />
      </div>
      <PendingVisitsModal
        open={showPendingVisitsModal}
        visits={quarantinedVisits}
        onSync={handleSyncQuarantined}
        onDiscard={handleDiscardQuarantined}
      />
      <PendingInvitationsModal
        open={showPendingInvitationsModal}
        invitations={quarantinedInvitations}
        onSync={handleSyncQuarantinedInvitations}
        onDiscard={handleDiscardQuarantinedInvitations}
      />
    </div>
  )
}

const mapStateToProps = state => ({
  enabled_visit_host: state.profile.enabled_visit_host,
})

const mapDispatchToProps = {
  dispatch,
  defaultSuccess,
  defaultCatch
}

export default connect(mapStateToProps, mapDispatchToProps)(LoadPage)
